import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  Renderer2,
} from '@angular/core';
import { animate, style, transition, trigger } from '@angular/animations';
import { LayoutService } from '@/core/layout/service/layout.service';
import { TooltipModule } from 'primeng/tooltip';
import { ButtonModule } from 'primeng/button';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { filter, Observable, Subscription } from 'rxjs';
import { selectUserProfile } from '@/store/current-user/current-user.selectors';
import { loadUserProfile } from '@/store/current-user/profile.actions';
import { UserProfile } from '@/features/my/dtos/user-profile.dto';
import { Store } from '@ngrx/store';

@Component({
  selector: '[app-menu-profile]',
  standalone: true,
  imports: [CommonModule, TooltipModule, ButtonModule, RouterModule],
  template: `
    <button
      (click)="toggleMenu()"
      pTooltip="Profile"
      [tooltipDisabled]="isTooltipDisabled()">
      
      <div *ngIf="hasProfileImage(); else noImage" class="flex flex-col items-center">
        <img 
          [src]="getProfileImageUrl()" 
          alt="Profile Image" 
          class="w-32 h-32 object-cover rounded-md circle" />
      </div>
      
      <ng-template #noImage>
        <div class="flex flex-col items-center justify-center bg-gray-100 aspect-square rounded-md mx-auto p-4 circle">
          <i class="pi pi-user text-gray-400 text-4xl"></i>
          <span class="text-gray-500 mt-2">NA</span>
        </div>
      </ng-template>
      
      <span class="text-start" *ngIf="profile">
        <strong>{{profile.firstName}} {{profile.lastName}}</strong>
      </span>
      
      <i
        class="layout-menu-profile-toggler pi pi-fw"
        [ngClass]="{
          'pi-angle-down': menuProfilePosition() === 'start' || isHorizontal(),
          'pi-angle-up': menuProfilePosition() === 'end' && !isHorizontal(),
        }">
      </i>
    </button>

    <ul
      *ngIf="menuProfileActive()"
      [@menu]="isHorizontal() ? 'overlay' : 'inline'">
      
      <li pTooltip="Profile" [tooltipDisabled]="isTooltipDisabled()">
        <button [routerLink]="['/my/profile']">
          <i class="pi pi-file-o pi-fw"></i>
          <span>Profile</span>
        </button>
      </li>
      
      <li
        pTooltip="Logout"
        [tooltipDisabled]="isTooltipDisabled()"
        [routerLink]="['/auth/logout']">
        <button class="p-link">
          <i class="pi pi-power-off pi-fw"></i>
          <span>Logout</span>
        </button>
      </li>
    </ul>
  `,
  animations: [
    trigger('menu', [
      transition('void => inline', [
        style({ height: 0 }),
        animate(
          '400ms cubic-bezier(0.86, 0, 0.07, 1)',
          style({ opacity: 1, height: '*' })
        ),
      ]),
      transition('inline => void', [
        animate(
          '400ms cubic-bezier(0.86, 0, 0.07, 1)',
          style({ opacity: 0, height: '0' })
        ),
      ]),
      transition('void => overlay', [
        style({ opacity: 0, transform: 'scaleY(0.8)' }),
        animate('.12s cubic-bezier(0, 0, 0.2, 1)'),
      ]),
      transition('overlay => void', [
        animate('.1s linear', style({ opacity: 0 })),
      ]),
    ]),
  ],
  host: {
    class: 'layout-menu-profile',
  },
  styles: `
    .circle {
      width: 60px;
      height: 60px;
      border-radius: 50%; 
    }
  `,
})
export class AppMenuProfile implements OnInit, OnDestroy {
  layoutService = inject(LayoutService);
  userProfile$!: Observable<UserProfile>;
  profile!: UserProfile | null;

  renderer = inject(Renderer2);
  el = inject(ElementRef);

  isHorizontal = computed(
    () => this.layoutService.isHorizontal() && this.layoutService.isDesktop()
  );

  menuProfileActive = computed(
    () => this.layoutService.layoutState().menuProfileActive
  );

  menuProfilePosition = computed(
    () => this.layoutService.layoutConfig().menuProfilePosition
  );

  isTooltipDisabled = computed(() => !this.layoutService.isSlim());

  subscription!: Subscription;

  outsideClickListener: any;
  constructor(
    private store: Store,) {
    this.subscription = this.layoutService.overlayOpen$.subscribe(() => {
      if (this.isHorizontal() && this.menuProfileActive()) {
        this.layoutService.layoutState.update((value) => ({
          ...value,
          menuProfileActive: false,
        }));
      }
    });

    effect(() => {
      if (
        this.isHorizontal() &&
        this.menuProfileActive() &&
        !this.outsideClickListener
      ) {
        this.bindOutsideClickListener();
      }

      if (!this.menuProfileActive() && this.isHorizontal()) {
        this.unbindOutsideClickListener();
      }
    });
  }

  ngOnInit(): void {

    this.userProfile$ = this.store.select(selectUserProfile).pipe(filter((profile): profile is UserProfile => profile !== null));
    this.userProfile$.subscribe((profile) => {
      this.profile = profile;
    });

    this.store.dispatch(loadUserProfile());
  }

  bindOutsideClickListener() {
    if (this.isHorizontal()) {
      this.outsideClickListener = this.renderer.listen(
        document,
        'click',
        (event: MouseEvent) => {
          if (this.menuProfileActive()) {
            const isOutsideClicked = !(
              this.el.nativeElement.isSameNode(event.target) ||
              this.el.nativeElement.contains(event.target)
            );
            if (isOutsideClicked) {
              this.layoutService.layoutState.update((value) => ({
                ...value,
                menuProfileActive: false,
              }));
            }
          }
        }
      );
    }
  }

  unbindOutsideClickListener() {
    if (this.outsideClickListener) {
      this.outsideClickListener();
      this.outsideClickListener = null;
    }
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.unbindOutsideClickListener();
  }

  toggleMenu() {
    this.layoutService.onMenuProfileToggle();
  }
  hasProfileImage(): boolean {
    const profileImage = this.profile?.profile?.profileImageUrl;
    return profileImage !== undefined && profileImage !== null && profileImage !== '';
  }

  getProfileImageUrl(): string {
    const profileImage = this.profile?.profile?.profileImageUrl;
    return profileImage || '';
  }
}
